import { useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';

/**
 * Freezes the outlet content at mount time so that AnimatePresence
 * can animate the exit of the old page while the new page enters.
 */
const FrozenOutlet = () => {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
};

const TRANSITION_DURATION = 0.4;
const EASE = [0.4, 0, 0.2, 1] as const;

/**
 * Variant functions receive the `custom` value (isForward boolean).
 * Using `custom` on AnimatePresence ensures the EXITING element
 * receives the latest direction, not the one from when it mounted.
 */
const variants: Variants = {
  enter: (isForward: boolean) => ({ x: isForward ? '100%' : '-100%' }),
  center: { x: 0 },
  exit: (isForward: boolean) => ({ x: isForward ? '-100%' : '100%' }),
};

export const AnimatedOutlet = () => {
  const location = useLocation();
  const { navigationDirection } = useQuestionnaireNavigation();
  const isForward = navigationDirection === 'forward';

  return (
    <AnimatePresence mode="sync" initial={false} custom={isForward}>
      <motion.div
        key={location.pathname}
        custom={isForward}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: TRANSITION_DURATION, ease: EASE }}
        className="absolute inset-0 overflow-y-auto no-scrollbar"
      >
        <FrozenOutlet />
      </motion.div>
    </AnimatePresence>
  );
};
